import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Save, Settings as SettingsIcon, RotateCcw, Loader2, Gift } from "lucide-react";

export default function AdminSettings() {
  const [settings, setSettings] = useState({
    referral_percent: 5,
    customer_discount_percent: 5,
    gst_percent: 18,
    invoice_prefix: "NPC",
    npc_state: "Gujarat",
    npc_gstin: "",
    npc_address: "",
    default_hsn_sac: "999316",
    maintenance_mode: false,
    maintenance_message: "NPC Platform is currently under maintenance. Please check again later.",
    maintenance_eta: "",
    maintenance_eta_at: "",
    maintenance_auto_disable: false,
    public_frontend_url: "https://npc.nirmalhospitals.in",
    admin_path: "/admin",
    admin_ip_whitelist: "",
    extension_enabled: true,
    extension_days: 15,
    notification_channels: {
      payment_success: "both",
      card_delivery: "both",
      invoice_delivery: "both",
      renewal_reminder: "both",
      kyc_reminder: "both",
      corporate_welcome: "both",
    },
    engagement_highly_active_visits: 6,
    engagement_highly_active_benefit_pct: 50,
    engagement_active_visits: 3,
    engagement_moderate_visits: 1,
    engagement_at_risk_days: 180,
    engagement_module_enabled: false,
  });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/settings");
        setSettings({
          referral_percent: r.data.referral_percent ?? 5,
          customer_discount_percent: r.data.customer_discount_percent ?? 5,
          gst_percent: r.data.gst_percent ?? 18,
          invoice_prefix: r.data.invoice_prefix ?? "NPC",
          npc_state: r.data.npc_state ?? "Gujarat",
          npc_gstin: r.data.npc_gstin ?? "",
          npc_address: r.data.npc_address ?? "",
          default_hsn_sac: r.data.default_hsn_sac ?? "999316",
          maintenance_mode: !!r.data.maintenance_mode,
          maintenance_message: r.data.maintenance_message ?? "NPC Platform is currently under maintenance. Please check again later.",
          maintenance_eta: r.data.maintenance_eta ?? "",
          maintenance_eta_at: r.data.maintenance_eta_at ?? "",
          maintenance_auto_disable: !!r.data.maintenance_auto_disable,
          public_frontend_url: r.data.public_frontend_url ?? "https://npc.nirmalhospitals.in",
          admin_path: r.data.admin_path ?? "/admin",
          admin_ip_whitelist: r.data.admin_ip_whitelist ?? "",
          extension_enabled: r.data.extension_enabled !== false,
          extension_days: r.data.extension_days ?? 15,
          notification_channels: r.data.notification_channels || {
            payment_success: "both",
            card_delivery: "both",
            invoice_delivery: "both",
            renewal_reminder: "both",
            kyc_reminder: "both",
            corporate_welcome: "both",
          },
          engagement_highly_active_visits: r.data.engagement_highly_active_visits ?? 6,
          engagement_highly_active_benefit_pct: r.data.engagement_highly_active_benefit_pct ?? 50,
          engagement_active_visits: r.data.engagement_active_visits ?? 3,
          engagement_moderate_visits: r.data.engagement_moderate_visits ?? 1,
          engagement_at_risk_days: r.data.engagement_at_risk_days ?? 180,
          engagement_module_enabled: !!r.data.engagement_module_enabled,
        });
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load settings");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings", {
        referral_percent: Number(settings.referral_percent),
        customer_discount_percent: Number(settings.customer_discount_percent),
        gst_percent: Number(settings.gst_percent),
        invoice_prefix: settings.invoice_prefix,
        npc_state: settings.npc_state,
        npc_gstin: settings.npc_gstin,
        npc_address: settings.npc_address,
        default_hsn_sac: settings.default_hsn_sac,
        maintenance_mode: !!settings.maintenance_mode,
        maintenance_message: settings.maintenance_message,
        maintenance_eta: settings.maintenance_eta,
        maintenance_eta_at: settings.maintenance_eta_at
          ? new Date(settings.maintenance_eta_at).toISOString()
          : "",
        maintenance_auto_disable: !!settings.maintenance_auto_disable,
        public_frontend_url: (settings.public_frontend_url || "").trim().replace(/\/+$/, ""),
        admin_path: (() => {
          let p = (settings.admin_path || "/admin").trim();
          if (!p.startsWith("/")) p = "/" + p;
          return p.replace(/\/+$/, "") || "/admin";
        })(),
        admin_ip_whitelist: (settings.admin_ip_whitelist || "").trim(),
        extension_enabled: !!settings.extension_enabled,
        extension_days: Math.max(0, parseInt(settings.extension_days || 0, 10)),
        notification_channels: settings.notification_channels,
        engagement_highly_active_visits: Math.max(1, parseInt(settings.engagement_highly_active_visits || 6, 10)),
        engagement_highly_active_benefit_pct: Math.min(100, Math.max(0, parseInt(settings.engagement_highly_active_benefit_pct || 50, 10))),
        engagement_active_visits: Math.max(1, parseInt(settings.engagement_active_visits || 3, 10)),
        engagement_moderate_visits: Math.max(1, parseInt(settings.engagement_moderate_visits || 1, 10)),
        engagement_at_risk_days: Math.max(1, parseInt(settings.engagement_at_risk_days || 180, 10)),
      });
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-sm text-[#5C6B7A]">Loading…</div>;
  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      <div>
        <h2 className="font-display text-3xl font-bold text-[#0F2540] flex items-center gap-2">
          <SettingsIcon className="h-6 w-6" /> Settings
        </h2>
        <p className="text-sm text-[#5C6B7A] mt-1">
          Controls how referrals are rewarded and how discounts are applied across the system.
        </p>
      </div>

      {/* ─── ENGAGEMENT MODULE MASTER SWITCH (top of Settings for visibility) ─── */}
      <div
        data-testid="settings-engagement-master-switch"
        className={`rounded-2xl border-2 p-5 ${settings.engagement_module_enabled ? "border-[#1F7A3D] bg-[#E5F2EA]" : "border-[#E1E8F0] bg-white"}`}
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <div className="flex items-center gap-2">
              <h3 className="font-display text-lg font-bold text-[#0F2540]">Engagement module · master switch</h3>
              {settings.engagement_module_enabled ? (
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#1F7A3D] text-white">Enabled</span>
              ) : (
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#5C6B7A] text-white">Disabled</span>
              )}
            </div>
            <p className="text-sm text-[#5C6B7A] mt-1">
              One-click toggle for the entire engagement layer — <strong>Plan Benefits</strong>, <strong>Visits</strong>, and <strong>QR Check-In</strong>.
              When disabled (the default), those sidebar entries are hidden from admins and customer-facing wallet/visit panels are suppressed. Backend data is preserved — toggling ON simply un-hides the UI.
            </p>
          </div>
          <label className="inline-flex items-center gap-3 cursor-pointer" data-testid="engagement-master-toggle-label">
            <span className="text-sm font-semibold text-[#0F2540]">
              {settings.engagement_module_enabled ? "ON" : "OFF"}
            </span>
            <input
              type="checkbox"
              checked={!!settings.engagement_module_enabled}
              onChange={(e) => setSettings({ ...settings, engagement_module_enabled: e.target.checked })}
              data-testid="engagement-master-toggle"
              className="sr-only peer"
            />
            <div className="relative w-14 h-8 bg-[#5C6B7A] rounded-full peer-checked:bg-[#1F7A3D] transition-colors after:content-[''] after:absolute after:left-1 after:top-1 after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-transform peer-checked:after:translate-x-6" />
          </label>
        </div>
        <button onClick={save} disabled={busy} data-testid="engagement-master-save" className="btn-primary mt-4 !text-xs !px-3 !py-2">
          {busy ? "Saving…" : `Save · ${settings.engagement_module_enabled ? "enable" : "disable"} module`}
        </button>
        <p className="text-[11px] text-[#5C6B7A] mt-2">
          Tip: after toggling, sidebar links update automatically within ~30 seconds. Hard-refresh the page for an immediate update.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-5 max-w-3xl">
        <Card
          label="Employee incentive %"
          desc="Percentage of plan price credited to the referring employee."
          value={settings.referral_percent}
          onChange={(v) => setSettings({ ...settings, referral_percent: v })}
          testid="setting-referral-percent"
          unit="%"
        />
        <Card
          label="Customer discount %"
          desc="Discount given to customers who use a referral code at checkout."
          value={settings.customer_discount_percent}
          onChange={(v) => setSettings({ ...settings, customer_discount_percent: v })}
          testid="setting-customer-discount-percent"
          unit="%"
        />
        <div className="rounded-2xl border border-[#E1E8F0] bg-white p-5 sm:col-span-2" data-testid="setting-extension-card">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-semibold text-[#0F2540]">Membership extension benefit</div>
              <div className="text-xs text-[#5C6B7A] mt-1">When a customer shares their NPC discount code and someone uses it, the sharer&apos;s membership is extended.</div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!settings.extension_enabled}
                onChange={(e) => setSettings({ ...settings, extension_enabled: e.target.checked })}
                className="h-4 w-4 accent-[#1E4F87]"
                data-testid="setting-extension-enabled"
              />
              <span className="text-xs uppercase tracking-wider text-[#5C6B7A]">{settings.extension_enabled ? "Enabled" : "Disabled"}</span>
            </label>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <label className="text-sm text-[#0F2540]">Extension days per use</label>
            <input
              type="number"
              min={0}
              max={365}
              step={1}
              value={settings.extension_days}
              onChange={(e) => setSettings({ ...settings, extension_days: e.target.value })}
              disabled={!settings.extension_enabled}
              className="w-24 rounded-xl border border-[#E1E8F0] px-3 py-2 text-sm disabled:bg-[#F4F7FB] disabled:opacity-60"
              data-testid="setting-extension-days"
            />
            <span className="text-xs text-[#5C6B7A]">days (e.g. 15, 30 — set 0 to disable)</span>
          </div>
        </div>
        <Card
          label="GST %"
          desc="Applied to all invoices (split 50/50 across CGST + SGST)."
          value={settings.gst_percent}
          onChange={(v) => setSettings({ ...settings, gst_percent: v })}
          testid="setting-gst-percent"
          unit="%"
        />
        <Card
          label="Invoice prefix"
          desc="Used in invoice numbers (e.g. NPC/2026-27/0001)."
          value={settings.invoice_prefix}
          onChange={(v) => setSettings({ ...settings, invoice_prefix: v })}
          testid="setting-invoice-prefix"
          type="text"
        />
      </div>

      <div>
        <h3 className="font-display text-xl font-bold text-[#0F2540] mt-8">GST & invoicing identity</h3>
        <p className="text-sm text-[#5C6B7A] mt-1">
          Drives intrastate (CGST + SGST) vs interstate (IGST) tax split and the GSTR-1 export. Customer state is captured at checkout.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 gap-5 max-w-3xl">
        <Card
          label="Business state (Place of supply)"
          desc="Hospital's registered state. Customers in this state → CGST + SGST. Others → IGST."
          value={settings.npc_state}
          onChange={(v) => setSettings({ ...settings, npc_state: v })}
          testid="setting-npc-state"
          type="text"
        />
        <Card
          label="Business GSTIN"
          desc="15-character GSTIN printed on every invoice header."
          value={settings.npc_gstin}
          onChange={(v) => setSettings({ ...settings, npc_gstin: v.toUpperCase() })}
          testid="setting-npc-gstin"
          type="text"
        />
        <Card
          label="Default HSN/SAC"
          desc="Fallback service code printed on invoice lines when a plan has none. Healthcare = 999316."
          value={settings.default_hsn_sac}
          onChange={(v) => setSettings({ ...settings, default_hsn_sac: v })}
          testid="setting-default-hsn"
          type="text"
        />
        <div className="rounded-2xl border border-[#E1E8F0] bg-white p-5 sm:col-span-1">
          <div className="text-sm font-semibold text-[#0F2540]">Registered address</div>
          <p className="text-xs text-[#5C6B7A] mt-1">Free-form. Appears on the printed invoice header.</p>
          <textarea
            rows={3}
            value={settings.npc_address}
            onChange={(e) => setSettings({ ...settings, npc_address: e.target.value })}
            className="mt-3 w-full rounded-xl border border-[#E1E8F0] bg-white px-3 py-2.5 text-sm focus:border-[#1E4F87] outline-none"
            data-testid="setting-npc-address"
          />
        </div>
      </div>

      <button onClick={save} disabled={busy} className="btn-primary" data-testid="settings-save-btn">
        <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save changes"}
      </button>

      {/* Public URLs & Security — super_admin only */}
      <div className="mt-10 pt-8 border-t border-[#E1E8F0]">
        <h3 className="font-display text-xl font-bold text-[#0F2540]">Public URLs &amp; Security</h3>
        <p className="text-sm text-[#5C6B7A] mt-1">Controls the public frontend URL used in QR codes &amp; emails, the obscured admin path, and IP-based access control. <b>Changes take effect immediately</b>.</p>
        <div className="mt-5 rounded-2xl border border-[#E1E8F0] bg-white p-5 max-w-3xl space-y-5">
          <div>
            <label className="text-xs font-semibold text-[#0F2540] uppercase tracking-wider">Public frontend URL</label>
            <input
              type="url"
              value={settings.public_frontend_url}
              onChange={(e) => setSettings({ ...settings, public_frontend_url: e.target.value })}
              placeholder="https://npc.nirmalhospitals.in"
              className="mt-2 w-full rounded-xl border border-[#E1E8F0] bg-white px-3 py-2.5 text-sm focus:border-[#1E4F87] outline-none font-mono"
              data-testid="setting-public-frontend-url"
            />
            <p className="text-[11px] text-[#5C6B7A] mt-1">Used in QR codes on membership cards and in welcome/login emails. No trailing slash.</p>
          </div>
          <div>
            <label className="text-xs font-semibold text-[#0F2540] uppercase tracking-wider">Admin URL path</label>
            <div className="mt-2 flex items-stretch gap-2">
              <input
                type="text"
                value={settings.admin_path}
                onChange={(e) => {
                  let v = e.target.value || "";
                  if (!v.startsWith("/")) v = "/" + v;
                  setSettings({ ...settings, admin_path: v });
                }}
                placeholder="/secure-admin-xyz"
                className="flex-1 rounded-xl border border-[#E1E8F0] bg-white px-3 py-2.5 text-sm focus:border-[#1E4F87] outline-none font-mono"
                data-testid="setting-admin-path"
              />
              <button
                type="button"
                onClick={() => {
                  // Generate /secure-admin-<8-char-hex> (random, no save until user clicks Save)
                  const bytes = new Uint8Array(4);
                  (window.crypto || window.msCrypto).getRandomValues(bytes);
                  const hex = Array.from(bytes).map(b => b.toString(16).padStart(2, "0")).join("");
                  setSettings({ ...settings, admin_path: `/secure-admin-${hex}` });
                }}
                className="btn-ghost text-xs whitespace-nowrap"
                data-testid="setting-admin-path-rotate"
                title="Generate a fresh obscure admin path"
              >
                ↻ Rotate
              </button>
            </div>
            <p className="text-[11px] text-[#5C6B7A] mt-1">
              <b className="text-[#A53A2A]">⚠ Strict mode</b> — once changed from <code>/admin</code>, the literal <code>/admin</code> URL returns 404. <b>Bookmark the new path before saving.</b> Tip: use <b>Rotate</b> quarterly as a security hygiene step.
            </p>
          </div>
          <div>
            <label className="text-xs font-semibold text-[#0F2540] uppercase tracking-wider">Admin IP whitelist</label>
            <input
              type="text"
              value={settings.admin_ip_whitelist}
              onChange={(e) => setSettings({ ...settings, admin_ip_whitelist: e.target.value })}
              placeholder="49.207.10.45, 10.0.0.0/24, 2401:4900::/32"
              className="mt-2 w-full rounded-xl border border-[#E1E8F0] bg-white px-3 py-2.5 text-sm focus:border-[#1E4F87] outline-none font-mono"
              data-testid="setting-admin-ip-whitelist"
            />
            <p className="text-[11px] text-[#5C6B7A] mt-1">
              Comma-separated list of IPs / CIDR blocks. Empty = no restriction. <b>Super-admin JWT bypasses this check</b> (escape hatch if you change networks).
            </p>
          </div>
          <button onClick={save} disabled={busy} className="btn-primary" data-testid="settings-security-save-btn">
            <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save URLs & Security"}
          </button>
        </div>
      </div>

      {/* Maintenance Mode — super_admin only via backend gate. Always render for visibility */}
      <div className="mt-10 pt-8 border-t border-[#E1E8F0]">
        <h3 className="font-display text-xl font-bold text-[#0F2540]">Maintenance mode</h3>
        <p className="text-sm text-[#5C6B7A] mt-1">
          Take the public site offline while you upgrade / patch. Admin login + admin panel keep working. Members and the public will see a friendly maintenance page.
        </p>
        <div className={`mt-5 rounded-2xl border p-5 max-w-3xl ${settings.maintenance_mode ? "border-[#A53A2A]/40 bg-[#A53A2A]/5" : "border-[#E1E8F0] bg-white"}`}>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={!!settings.maintenance_mode}
              onChange={(e) => setSettings({ ...settings, maintenance_mode: e.target.checked })}
              className="mt-1 h-5 w-5 accent-[#A53A2A]"
              data-testid="setting-maintenance-toggle"
            />
            <div>
              <div className="text-sm font-semibold text-[#0F2540]">
                {settings.maintenance_mode ? "Maintenance ON — public site is offline" : "Maintenance OFF — site is LIVE"}
              </div>
              <p className="text-xs text-[#5C6B7A] mt-1">
                Toggle requires Save to take effect. Members see the message below; admins are unaffected.
              </p>
            </div>
          </label>
          <div className="mt-4">
            <label className="text-xs font-semibold text-[#0F2540] uppercase tracking-wider">Maintenance message</label>
            <textarea
              rows={3}
              value={settings.maintenance_message}
              onChange={(e) => setSettings({ ...settings, maintenance_message: e.target.value })}
              placeholder="NPC Platform is currently under maintenance. Please check again later."
              className="mt-2 w-full rounded-xl border border-[#E1E8F0] bg-white px-3 py-2.5 text-sm focus:border-[#1E4F87] outline-none"
              data-testid="setting-maintenance-message"
            />
          </div>
          <div className="mt-3">
            <label className="text-xs font-semibold text-[#0F2540] uppercase tracking-wider">Estimated availability (free-text fallback)</label>
            <input
              type="text"
              value={settings.maintenance_eta}
              onChange={(e) => setSettings({ ...settings, maintenance_eta: e.target.value })}
              placeholder="e.g. Back online in ~30 minutes"
              className="mt-2 w-full rounded-xl border border-[#E1E8F0] bg-white px-3 py-2.5 text-sm focus:border-[#1E4F87] outline-none"
              data-testid="setting-maintenance-eta"
            />
            <p className="text-[11px] text-[#5C6B7A] mt-1">Shown only when ETA date/time below is not set.</p>
          </div>
          <div className="mt-3">
            <label className="text-xs font-semibold text-[#0F2540] uppercase tracking-wider">ETA — reopen date &amp; time (live countdown)</label>
            <input
              type="datetime-local"
              value={(() => {
                const v = settings.maintenance_eta_at;
                if (!v) return "";
                const d = new Date(v);
                if (isNaN(d.getTime())) return "";
                // Convert UTC ISO → local datetime-local value (YYYY-MM-DDTHH:mm)
                const pad = (n) => String(n).padStart(2, "0");
                return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
              })()}
              onChange={(e) => {
                const v = e.target.value;
                setSettings({ ...settings, maintenance_eta_at: v ? new Date(v).toISOString() : "" });
              }}
              className="mt-2 w-full rounded-xl border border-[#E1E8F0] bg-white px-3 py-2.5 text-sm focus:border-[#1E4F87] outline-none"
              data-testid="setting-maintenance-eta-at"
            />
            <p className="text-[11px] text-[#5C6B7A] mt-1">When set, the public maintenance page shows a live Days · Hours · Min · Sec countdown to this time.</p>
          </div>
          <label className="mt-4 flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={!!settings.maintenance_auto_disable}
              onChange={(e) => setSettings({ ...settings, maintenance_auto_disable: e.target.checked })}
              className="mt-0.5 h-4 w-4 accent-[#1F7A3D]"
              data-testid="setting-maintenance-auto-disable"
            />
            <div>
              <div className="text-sm font-semibold text-[#0F2540]">Auto-disable when ETA expires</div>
              <p className="text-[11px] text-[#5C6B7A] mt-0.5">Site automatically goes LIVE the moment the countdown reaches zero. No manual intervention required.</p>
            </div>
          </label>
          <button onClick={save} disabled={busy} className="btn-primary mt-5" data-testid="settings-maintenance-save-btn">
            <Save className="h-4 w-4" /> {busy ? "Saving…" : settings.maintenance_mode ? "Enable maintenance mode" : "Disable maintenance mode"}
          </button>
        </div>
      </div>

      {/* ──────────────────────────────────────────────────────────────────
          Notification Channel Routing — per-event toggle between
          Email / WhatsApp / Both / None. Each event's selection is
          honoured by the auto-trigger sites in real-time.
          ────────────────────────────────────────────────────────────── */}
      <div className="mt-12" data-testid="settings-notifications">
        <h2 className="font-display text-2xl font-bold text-[#0F2540]">Notification channels</h2>
        <p className="text-sm text-[#5C6B7A] mt-1">Choose how each event reaches members — email, WhatsApp, both, or none.</p>
        <div className="mt-6 rounded-2xl border border-[#E1E8F0] bg-white divide-y divide-[#E1E8F0]">
          {[
            { key: "payment_success",   label: "Payment success",     desc: "Receipt + amount + new validity date." },
            { key: "card_delivery",     label: "Card delivery",       desc: "Lets the primary member know cards are ready in dashboard." },
            { key: "invoice_delivery",  label: "Invoice delivery",    desc: "Tax invoice ready to download." },
            { key: "renewal_reminder",  label: "Renewal reminder",    desc: "Sent D-30 / D-15 / D-7 before expiry." },
            { key: "kyc_reminder",      label: "KYC reminder",        desc: "Nudge primary member to complete KYC." },
            { key: "corporate_welcome", label: "Corporate onboarding", desc: "Sent on corporate-bulk activation." },
          ].map((ev) => (
            <div key={ev.key} className="px-5 py-4 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-[#0F2540]">{ev.label}</div>
                <p className="text-xs text-[#5C6B7A] mt-0.5">{ev.desc}</p>
              </div>
              <div
                className="inline-flex rounded-full bg-[#F0F4FA] p-1 border border-[#E1E8F0] shrink-0"
                role="radiogroup"
                aria-label={`Channel for ${ev.label}`}
              >
                {["both", "email", "whatsapp", "none"].map((c) => {
                  const active = (settings.notification_channels?.[ev.key] || "both") === c;
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setSettings((s) => ({
                        ...s,
                        notification_channels: { ...(s.notification_channels || {}), [ev.key]: c },
                      }))}
                      className={`px-3 py-1 text-[11px] uppercase tracking-wider font-semibold rounded-full transition-colors ${active ? "bg-[#0F2540] text-white shadow-sm" : "text-[#0F2540] hover:bg-white"}`}
                      data-testid={`settings-channel-${ev.key}-${c}`}
                    >
                      {c === "both" ? "Both" : c === "email" ? "Email" : c === "whatsapp" ? "WhatsApp" : "None"}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <button onClick={save} disabled={busy} className="btn-primary mt-5" data-testid="settings-notifications-save">
          <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save notification channels"}
        </button>
      </div>

      {/* Referral extension — historical backfill tool */}
      <ReferralBackfillCard />

      {/* ──────────────────────────────────────────────────────────────────
          Engagement Score Thresholds — 5-tier scoring (Highly Active …
          Inactive). Editable so the hospital can re-tune what "engaged"
          means for their member base over time.
          ────────────────────────────────────────────────────────────── */}
      <div className="mt-12" data-testid="settings-engagement">
        <h2 className="font-display text-2xl font-bold text-[#0F2540]">Engagement score thresholds</h2>
        <p className="text-sm text-[#5C6B7A] mt-1">
          Tune what counts as Highly Active / Active / Moderate / At Risk based on visits in the last 12 months. Changes take effect immediately for new dashboard reads.
        </p>
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <ThresholdCard
            label="Highly Active · min visits / yr"
            desc="6+ visits in the last 12 months (combined with the benefit % below)."
            help="Sensible range: 4–24 (≈ monthly to bi-monthly)"
            min={1} max={52}
            value={settings.engagement_highly_active_visits}
            onChange={(v) => setSettings({ ...settings, engagement_highly_active_visits: v })}
            testid="setting-eng-highly-active-visits"
            tone="emerald"
          />
          <ThresholdCard
            label="Highly Active · min benefit usage %"
            desc="The member must have used at least this % of their allocated benefits."
            help="0–100 percent"
            unit="%"
            min={0} max={100}
            value={settings.engagement_highly_active_benefit_pct}
            onChange={(v) => setSettings({ ...settings, engagement_highly_active_benefit_pct: v })}
            testid="setting-eng-highly-active-benefit-pct"
            tone="emerald"
          />
          <ThresholdCard
            label="Active · min visits / yr"
            desc="Member visited at least this many times in the last 12 months."
            help="Must be lower than Highly Active threshold"
            min={1} max={52}
            value={settings.engagement_active_visits}
            onChange={(v) => setSettings({ ...settings, engagement_active_visits: v })}
            testid="setting-eng-active-visits"
            tone="navy"
          />
          <ThresholdCard
            label="Moderate · min visits / yr"
            desc="Below Active but at least this many visits in 12 months."
            help="Must be lower than Active threshold"
            min={1} max={52}
            value={settings.engagement_moderate_visits}
            onChange={(v) => setSettings({ ...settings, engagement_moderate_visits: v })}
            testid="setting-eng-moderate-visits"
            tone="gold"
          />
          <ThresholdCard
            label="At Risk · days since last visit"
            desc="Member is flagged At Risk if their last visit was older than this many days."
            help="Recommended 90–365 days"
            unit="days"
            min={1} max={730}
            value={settings.engagement_at_risk_days}
            onChange={(v) => setSettings({ ...settings, engagement_at_risk_days: v })}
            testid="setting-eng-at-risk-days"
            tone="ember"
          />
        </div>
        <button onClick={save} disabled={busy} className="btn-primary mt-5" data-testid="settings-engagement-save">
          <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save engagement thresholds"}
        </button>
      </div>
    </div>
  );
}

function Card({ label, desc, value, onChange, testid, unit = "", type = "number" }) {
  return (
    <div className="rounded-2xl border border-[#E1E8F0] bg-white p-5">
      <div className="text-sm font-semibold text-[#0F2540]">{label}</div>
      <p className="text-xs text-[#5C6B7A] mt-1">{desc}</p>
      <div className="mt-3 flex items-center gap-2">
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 rounded-xl border border-[#E1E8F0] bg-white px-3 py-2.5 text-base font-semibold focus:border-[#1E4F87] outline-none"
          data-testid={testid}
        />
        {unit && <span className="text-[#5C6B7A] font-bold">{unit}</span>}
      </div>
    </div>
  );
}

function ReferralBackfillCard() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/referrals/backfill-preview");
      setPreview(r.data);
    } catch (e) {
      toast.error(apiErr(e, "Could not load backfill preview"));
      setPreview(null);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const apply = async () => {
    setApplying(true);
    setConfirmOpen(false);
    try {
      const r = await api.post("/admin/referrals/backfill-apply", {});
      const n = r.data?.granted ?? 0;
      const d = r.data?.days_per_grant ?? 0;
      toast.success(`Backfill complete · ${n} member${n === 1 ? "" : "s"} extended by ${d}d each`);
      await load();
    } catch (e) {
      toast.error(apiErr(e, "Backfill failed"));
    } finally { setApplying(false); }
  };

  const totals = preview?.totals || {};
  const candidates = preview?.candidates || [];

  return (
    <div className="mt-12" data-testid="settings-referral-backfill">
      <h2 className="font-display text-2xl font-bold text-[#0F2540] flex items-center gap-2">
        <Gift className="h-5 w-5 text-[#C89B3C]" /> Referral extension · auto-backfill
      </h2>
      <p className="text-sm text-[#5C6B7A] mt-1">
        Past customers whose referrals were silently dropped (legacy purchase route, pre-Iter48 fix) can be retroactively extended here.
        This is <strong>idempotent</strong> — re-running grants 0 because each successful grant writes an audit log that the next run sees.
      </p>

      <div className="mt-4 rounded-2xl border border-[#E1E8F0] bg-white p-5">
        {loading ? (
          <div className="text-sm text-[#5C6B7A]"><Loader2 className="h-4 w-4 inline animate-spin" /> Scanning past referrals…</div>
        ) : !preview?.enabled ? (
          <div className="text-sm text-[#A53A2A]">Referral extension is disabled or set to 0 days. Enable it under <em>Referrals & discount</em> first.</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <BackfillStat label="Pending grants" value={totals.to_grant ?? 0} tone="emerald" testid="backfill-stat-to-grant" />
              <BackfillStat label={`× ${totals.days_per_grant ?? 0} days each`} value={`${totals.total_days ?? 0}d`} tone="navy" testid="backfill-stat-total-days" />
              <BackfillStat label="Already granted" value={totals.skipped_already_granted ?? 0} tone="gray" testid="backfill-stat-skipped-already" />
              <BackfillStat label="No sharer" value={totals.skipped_no_sharer ?? 0} tone="gray" testid="backfill-stat-skipped-no-sharer" sub="test/employee codes" />
            </div>

            {candidates.length > 0 && (
              <details className="mt-4">
                <summary className="cursor-pointer text-sm font-semibold text-[#1E4F87] hover:underline" data-testid="backfill-details-toggle">
                  Show the {candidates.length} member{candidates.length === 1 ? "" : "s"} that will be extended →
                </summary>
                <div className="mt-3 max-h-72 overflow-y-auto rounded-xl border border-[#E1E8F0]">
                  <table className="w-full text-xs">
                    <thead className="bg-[#F7F9FC] text-[#5C6B7A] uppercase tracking-wider">
                      <tr>
                        <th className="text-left px-3 py-2">Referrer</th>
                        <th className="text-left px-3 py-2">Current valid till</th>
                        <th className="text-left px-3 py-2">Got referred by</th>
                        <th className="text-left px-3 py-2">Plan · Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {candidates.map((c, i) => (
                        <tr key={i} className="border-t border-[#E1E8F0]" data-testid={`backfill-row-${i}`}>
                          <td className="px-3 py-2">
                            <div className="font-semibold text-[#0F2540]">{c.sharer_name || "—"}</div>
                            <div className="font-mono text-[#5C6B7A]">{c.sharer_member_id}</div>
                          </td>
                          <td className="px-3 py-2 text-[#0F2540]">
                            {c.sharer_current_valid_till ? new Date(c.sharer_current_valid_till).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                          </td>
                          <td className="px-3 py-2">
                            <div className="font-semibold text-[#0F2540]">{c.referee_name || "—"}</div>
                            <div className="font-mono text-[#5C6B7A]">{c.referee_member_id}</div>
                          </td>
                          <td className="px-3 py-2 text-[#5C6B7A]">
                            {c.referee_plan_name}
                            <br />
                            {c.referee_issued_at ? new Date(c.referee_issued_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" }) : ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            <div className="mt-5 flex flex-wrap items-center gap-3">
              {totals.to_grant > 0 ? (
                <button
                  onClick={() => setConfirmOpen(true)}
                  disabled={applying}
                  data-testid="backfill-apply-btn"
                  className="btn-primary"
                >
                  {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4" />}
                  {applying ? "Granting…" : `Grant ${totals.to_grant} extensions now`}
                </button>
              ) : (
                <span className="text-sm text-[#1F7A3D] font-semibold">All historical referrals already granted ✓</span>
              )}
              <button onClick={load} disabled={loading} className="btn-ghost !text-xs">
                <RotateCcw className="h-3.5 w-3.5" /> Re-scan
              </button>
            </div>
          </>
        )}
      </div>

      {/* Confirm modal */}
      {confirmOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setConfirmOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-md p-6" data-testid="backfill-confirm-modal">
            <h3 className="font-display text-lg font-bold text-[#0F2540]">Confirm backfill</h3>
            <p className="text-sm text-[#5C6B7A] mt-2">
              This will extend <strong>{totals.to_grant}</strong> referrer membership{totals.to_grant === 1 ? "" : "s"} by{" "}
              <strong>{totals.days_per_grant} days</strong> each (<strong>{totals.total_days} day-equivalents</strong> total).
              Each grant is logged in Admin Logs for auditability and cannot be silently reversed.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setConfirmOpen(false)} className="btn-ghost">Cancel</button>
              <button onClick={apply} disabled={applying} data-testid="backfill-confirm-yes" className="btn-primary">
                {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4" />}
                Yes, grant extensions
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BackfillStat({ label, value, sub, tone = "navy", testid }) {
  const cls = {
    navy: "bg-[#EAF1FA] text-[#1E4F87] border-[#E1E8F0]",
    emerald: "bg-[#E5F2EA] text-[#1F7A3D] border-[#C7E2D2]",
    gray: "bg-[#F7F9FC] text-[#5C6B7A] border-[#E1E8F0]",
  }[tone] || "bg-[#EAF1FA] text-[#1E4F87] border-[#E1E8F0]";
  return (
    <div className={`rounded-xl border p-3 ${cls}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider opacity-80">{label}</div>
      <div className="font-display text-xl font-bold text-[#0F2540]">{value}</div>
      {sub && <div className="text-[10px] opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
}

function ThresholdCard({ label, desc, value, onChange, testid, unit = "", tone = "navy", min = 0, max = 999, help }) {
  const accent = {
    emerald: "border-l-[#1F7A3D]",
    navy: "border-l-[#1E4F87]",
    gold: "border-l-[#C89B3C]",
    ember: "border-l-[#A53A2A]",
  }[tone] || "border-l-[#1E4F87]";
  const v = Number(value);
  const oob = !Number.isNaN(v) && (v < min || v > max);
  return (
    <div className={`rounded-2xl border border-[#E1E8F0] bg-white p-5 border-l-4 ${accent}`}>
      <div className="text-sm font-semibold text-[#0F2540]">{label}</div>
      <p className="text-xs text-[#5C6B7A] mt-1">{desc}</p>
      <div className="mt-3 flex items-center gap-2">
        <input
          type="number"
          min={min}
          max={max}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`flex-1 rounded-xl border bg-white px-3 py-2.5 text-base font-semibold focus:border-[#1E4F87] outline-none ${oob ? "border-[#A53A2A]" : "border-[#E1E8F0]"}`}
          data-testid={testid}
        />
        {unit && <span className="text-[#5C6B7A] font-bold">{unit}</span>}
      </div>
      {help && <p className="text-[10px] text-[#5C6B7A] mt-1.5">{help}</p>}
      {oob && <p className="text-[10px] text-[#A53A2A] mt-1.5 font-semibold">Allowed range: {min}–{max}</p>}
    </div>
  );
}
